import { forwardRef, useEffect, useImperativeHandle, useRef, useState, type ReactNode } from 'react';
import { DescriptionOutlined } from '@mui/icons-material';
import { EmptyState, LoadingState } from '@practium/ui';
import { renderPdfPagesToImages } from '../../../utils/renderPdfPages';
import styles from './ReportViewer.module.css';

export interface ReportViewerHandle {
	print: () => void;
	download: (fileName: string) => void;
}

export interface ReportViewerProps {
	data: Blob | undefined;
	isLoading: boolean;
	isError: boolean;
	emptyState?: ReactNode;
	documentLabel: string;
}

export const ReportViewer = forwardRef<ReportViewerHandle, ReportViewerProps>(
	({ data, isLoading, isError, emptyState, documentLabel }, ref) => {
		const [pdfUrl, setPdfUrl] = useState('');
		const [pageImages, setPageImages] = useState<string[]>([]);
		const [isRenderingPages, setIsRenderingPages] = useState(false);
		const [hasRenderError, setHasRenderError] = useState(false);
		const printFrameRef = useRef<HTMLIFrameElement>(null);

		useEffect(() => {
			if (!data) {
				setPdfUrl('');
				return;
			}

			const url = URL.createObjectURL(data);
			setPdfUrl(url);
			return () => URL.revokeObjectURL(url);
		}, [data]);

		useEffect(() => {
			if (!data) {
				setPageImages([]);
				return;
			}

			let cancelled = false;
			setIsRenderingPages(true);
			setHasRenderError(false);

			renderPdfPagesToImages(data)
				.then((images) => {
					if (!cancelled) setPageImages(images);
				})
				.catch(() => {
					if (!cancelled) setHasRenderError(true);
				})
				.finally(() => {
					if (!cancelled) setIsRenderingPages(false);
				});

			return () => {
				cancelled = true;
			};
		}, [data]);

		useImperativeHandle(ref, () => ({
			print: () => printFrameRef.current?.contentWindow?.print(),
			download: (fileName: string) => {
				if (!pdfUrl) return;
				const link = document.createElement('a');
				link.href = pdfUrl;
				link.download = fileName;
				link.click();
			},
		}));

		const isReportLoading = isLoading || isRenderingPages;

		return (
			<>
				<div className={styles.content}>
					{emptyState ??
						(isReportLoading ? (
							<div className={styles.loading}>
								<LoadingState />
							</div>
						) : isError || hasRenderError ? (
							<EmptyState
								icon={<DescriptionOutlined />}
								title="Não foi possível carregar o documento"
								description="Tente novamente mais tarde."
							/>
						) : pageImages.length > 0 ? (
							<div className={styles.pages}>
								{pageImages.map((image, index) => (
									<img
										key={image}
										src={image}
										alt={`${documentLabel} - página ${index + 1}`}
										className={styles.pageImage}
									/>
								))}
							</div>
						) : (
							<div className={styles.placeholder} />
						))}
				</div>
				{pdfUrl && (
					<iframe
						ref={printFrameRef}
						src={pdfUrl}
						title={`Impressão - ${documentLabel}`}
						className={styles.printFrame}
					/>
				)}
			</>
		);
	}
);

ReportViewer.displayName = 'ReportViewer';
